import type {
  CreateFeedbackInput,
  FeedbackComment,
  FeedbackItem,
  FinalishClientOptions,
  FinalishSessionKind,
  FinalishWidgetSession,
  FinalishWidgetSessionIdentityInput,
  GuestBootstrap,
  GuestSession,
  ReviewSessionBootstrap,
  ReviewPage,
  WidgetSessionBootstrap,
} from './types';

export class FinalishApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'FinalishApiError';
  }
}

export class FinalishClient {
  private readonly apiUrl: string;
  private readonly request: typeof fetch;
  private sessionToken?: string;
  private sessionKind: FinalishSessionKind;

  constructor(options: FinalishClientOptions) {
    this.apiUrl = options.apiUrl.replace(/\/+$/, '');
    this.request = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.sessionToken = options.sessionToken;
    this.sessionKind = options.sessionKind ?? 'guest';
  }

  setSessionToken(token: string, kind: FinalishSessionKind = this.sessionKind): void {
    this.sessionToken = token;
    this.sessionKind = kind;
  }

  async exchangeWidgetSession(input: {
    projectId: string;
    targetUrl: string;
    identity: FinalishWidgetSessionIdentityInput;
  }): Promise<FinalishWidgetSession> {
    const session = await this.send<FinalishWidgetSession>(
      '/api/v1/widget-sessions',
      { method: 'POST', body: JSON.stringify(input) },
      false,
    );
    this.sessionToken = session.sessionToken;
    this.sessionKind = 'widget';
    return session;
  }

  async exchangeGuestLink(input: {
    linkToken: string;
    displayName: string;
    email?: string;
  }): Promise<GuestSession> {
    const session = await this.send<GuestSession>(
      '/api/v1/guest/sessions',
      { method: 'POST', body: JSON.stringify(input) },
      false,
    );
    this.sessionToken = session.sessionToken;
    return session;
  }

  bootstrap(): Promise<
    GuestBootstrap | ReviewSessionBootstrap | WidgetSessionBootstrap
  > {
    return this.send(this.sessionPath('/bootstrap'));
  }

  resolvePage(input: {
    title: string;
    url: string;
  }): Promise<ReviewPage> {
    return this.send(this.sessionPath('/pages/resolve'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  createFeedback(input: CreateFeedbackInput): Promise<FeedbackItem> {
    return this.send(this.sessionPath('/feedback'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  listFeedback(query: {
    pageId: string;
    status?: 'open' | 'resolved';
  }): Promise<FeedbackItem[]> {
    const params = new URLSearchParams();
    params.set('pageId', query.pageId);
    if (query.status) params.set('status', query.status);
    const suffix = params.size > 0 ? `?${params}` : '';
    return this.send(`${this.sessionPath('/feedback')}${suffix}`);
  }

  addComment(
    feedbackItemId: string,
    pageId: string,
    body: string,
  ): Promise<FeedbackComment> {
    const params = new URLSearchParams({ pageId });
    return this.send(
      this.sessionPath(
        `/feedback/${encodeURIComponent(feedbackItemId)}/comments?${params}`,
      ),
      {
        method: 'POST',
        body: JSON.stringify({ body }),
      },
    );
  }

  listComments(
    feedbackItemId: string,
    pageId: string,
  ): Promise<FeedbackComment[]> {
    const params = new URLSearchParams({ pageId });
    return this.send(
      this.sessionPath(
        `/feedback/${encodeURIComponent(feedbackItemId)}/comments?${params}`,
      ),
    );
  }

  private sessionPath(path: string): string {
    const root = {
      guest: 'guest',
      review: 'review-session',
      widget: 'widget-session',
    }[this.sessionKind];
    return `/api/v1/${root}${path}`;
  }

  private async send<T>(
    path: string,
    init: RequestInit = {},
    authenticated = true,
  ): Promise<T> {
    if (authenticated && !this.sessionToken) {
      throw new FinalishApiError(
        401,
        `${this.sessionKind}_session_missing`,
        'Create a browser session before calling this method',
      );
    }
    const response = await this.request(`${this.apiUrl}${path}`, {
      ...init,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(authenticated
          ? { Authorization: `Bearer ${this.sessionToken}` }
          : {}),
        ...init.headers,
      },
    });
    const payload = (await response.json()) as Record<string, unknown>;
    if (!response.ok) {
      throw new FinalishApiError(
        response.status,
        typeof payload.code === 'string' ? payload.code : 'request_failed',
        typeof payload.message === 'string'
          ? payload.message
          : 'Finalish API request failed',
      );
    }
    return payload as T;
  }
}
